import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '麻将在线玩',
    url: 'https://www.majiang-online.com/',
    description: '麻将在线玩是一个免费的在线麻将游戏平台，用户无需下载安装，打开浏览器即可畅玩经典麻将游戏。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@majiang-online.com',
      availableLanguage: ['Chinese'],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.majiang-online.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  },
];

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 麻将在线玩免费网页版 | 品牌介绍与联系方式"
        description="麻将在线玩是专业的免费在线麻将游戏平台。了解我们的品牌故事、发展历程和核心优势。提供国标麻将、四川麻将等多种玩法，无需下载即玩。"
        keywords="麻将在线玩关于我们,麻将游戏平台,在线麻将,麻将在线玩免费网页版"
        canonicalUrl="https://www.majiang-online.com/about"
        schemaData={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-400">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li className="text-foreground-700 font-medium">关于我们</li>
          </ol>
        </nav>

        <section className="w-full py-12 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 md:gap-16 items-center">
              <div className="lg:w-1/2">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">
                  关于麻将在线玩
                </h1>
                <div className="space-y-4 text-base md:text-lg text-foreground-600 leading-relaxed">
                  <p>
                    麻将在线玩成立于2020年，是国内领先的免费在线麻将游戏平台。我们致力于为麻将爱好者提供最便捷、最丰富的在线麻将体验——无需下载安装，打开浏览器即可畅玩。
                  </p>
                  <p>
                    平台汇聚了国标麻将、四川麻将、广东麻将、血流成河、长沙麻将、东北麻将等十余种主流玩法，满足不同地区玩家的需求。无论是单机练习还是与好友联机对战，麻将在线玩都能让你随时随地享受麻将乐趣。
                  </p>
                  <p>
                    截至2026年，麻将在线玩已拥有超过500万注册用户，日均活跃用户超过30万。我们始终坚持以用户体验为核心，不断优化游戏性能和玩法体验，深受广大麻将爱好者的喜爱和信赖。
                  </p>
                </div>
              </div>
              <div className="lg:w-1/2 w-full">
                <div className="relative h-72 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20modern%20office%20workspace%20with%20warm%20lighting%2C%20minimalist%20Chinese%20aesthetic%2C%20wooden%20desk%20with%20laptop%2C%20subtle%20burgundy%20and%20gold%20decorative%20accents%2C%20professional%20atmosphere%2C%20clean%20composition%2C%20high%20quality%20commercial%20photography&width=800&height=500&seq=about-hero-image&orientation=landscape"
                    alt="麻将在线玩品牌介绍"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              为什么选择麻将在线玩
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '完全免费',
                  desc: '平台所有玩法均永久免费，无隐藏收费，无需充值即可畅玩所有麻将玩法。',
                  color: 'bg-primary-100/70 text-primary-600',
                },
                {
                  icon: 'ri-global-line',
                  title: '即点即玩',
                  desc: '纯网页版游戏，无需下载安装任何软件或插件，打开浏览器即可快速开局。',
                  color: 'bg-accent-100/70 text-accent-600',
                },
                {
                  icon: 'ri-stack-line',
                  title: '玩法丰富',
                  desc: '涵盖国标麻将、四川麻将、广东麻将等十余种主流玩法，满足不同玩家需求。',
                  color: 'bg-secondary-100/70 text-secondary-600',
                },
                {
                  icon: 'ri-group-line',
                  title: '多人联机',
                  desc: '支持创建私人房间邀请好友对战，也可随机匹配与全国麻友切磋技艺。',
                  color: 'bg-primary-100/70 text-primary-600',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '多端适配',
                  desc: '支持电脑、平板、手机等设备，随时随地登录账号继续未完成的牌局。',
                  color: 'bg-accent-100/70 text-accent-600',
                },
                {
                  icon: 'ri-heart-line',
                  title: '安全可靠',
                  desc: '采用先进的加密技术和公平算法，确保游戏环境公正透明，保护玩家权益。',
                  color: 'bg-secondary-100/70 text-secondary-600',
                },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${item.color} mb-4`}>
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2"></div>
              {[
                { year: '2026', title: '用户突破500万', desc: '平台注册用户超过500万，日均活跃用户30万+，成为国内领先的在线麻将平台。' },
                { year: '2025', title: '全新版本发布', desc: '推出血流成河、长沙麻将等新玩法，优化多人联机体验，界面全面升级。' },
                { year: '2023', title: '移动端适配', desc: '全面适配手机和平板设备，玩家随时随地都能畅享麻将乐趣。' },
                { year: '2022', title: '多人联机上线', desc: '支持创建私人房间和随机匹配，好友对战功能正式上线。' },
                { year: '2020', title: '平台成立', desc: '麻将在线玩正式上线，以国标麻将和四川麻将为核心玩法开始运营。' },
              ].map((item, idx) => (
                <div key={item.year} className={`relative flex items-start gap-6 mb-8 ${
                  idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}>
                  <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold z-10 md:mx-auto md:absolute md:left-1/2 md:-translate-x-1/2">
                    {item.year.slice(2)}
                  </div>
                  <div className={`flex-1 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                      <span className="text-xs text-primary-500 font-bold">{item.year}</span>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mt-1 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-base text-foreground-500 leading-relaxed mb-8">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的客服团队将尽快回复您。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-400">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-700">support@majiang-online.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/70 text-accent-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-400">客服电话</p>
                      <p className="text-sm font-medium text-foreground-700">400-888-6688</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100/70 text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-400">公司地址</p>
                      <p className="text-sm font-medium text-foreground-700">北京市海淀区中关村科技园A座</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">发送消息</h3>
                <form action="https://readdy.ai/api/form/d9s8nbsnlsngrm4lmi8g" method="POST" className="flex flex-col gap-4" data-readdy-form>
                  <div>
                    <label htmlFor="about-name" className="block text-sm text-foreground-600 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm text-foreground-600 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm text-foreground-600 mb-1.5">留言内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}